import { useQueryClient } from '@tanstack/react-query'
import { useRouter } from '@tanstack/react-router'
import { useCallback } from 'react'

import { logout, logoutAllDevices } from '@/api/auth'

/**
 * Signing out, wherever the control sits.
 *
 * Three things have to happen together and none of them is optional: the
 * tokens go (in `logout` itself, even when the request fails), the query cache
 * goes — it outlives the session otherwise, and the next sign-in on this
 * browser would render the previous user's rows before refetching — and the
 * app leaves the authenticated area. A second copy of this on another screen
 * did two of the three.
 *
 * `everywhere` ends every session of this account rather than this one — the
 * same three things follow either way, which is why it is a flag here and not
 * a second path somewhere else.
 */
export function useLogout() {
  const router = useRouter()
  const queryClient = useQueryClient()

  return useCallback(
    (options?: { everywhere?: boolean }) => {
      const request = options?.everywhere ? logoutAllDevices() : logout()
      void request.finally(() => {
        queryClient.clear()
        router.navigate({ to: '/login' })
      })
    },
    [queryClient, router],
  )
}
